const CHART_JS_URL = 'https://cdn.jsdelivr.net/npm/chart.js@4.5.1/dist/chart.umd.min.js';

const chartInstances = new Map();
let chartLoaderPromise = null;
let themeObserver = null;

const loadChartJs = () => {
    if (window.Chart) {
        return Promise.resolve(window.Chart);
    }

    if (chartLoaderPromise) {
        return chartLoaderPromise;
    }

    chartLoaderPromise = new Promise((resolve, reject) => {
        const existing = document.querySelector('script[data-kupana-chartjs]');
        if (existing) {
            existing.addEventListener('load', () => resolve(window.Chart), { once: true });
            existing.addEventListener('error', () => reject(new Error('Chart.js konnte nicht geladen werden.')), { once: true });
            return;
        }

        const script = document.createElement('script');
        script.src = CHART_JS_URL;
        script.async = true;
        script.crossOrigin = 'anonymous';
        script.referrerPolicy = 'no-referrer';
        script.dataset.kupanaChartjs = 'true';
        script.addEventListener('load', () => {
            if (window.Chart) {
                resolve(window.Chart);
                return;
            }
            reject(new Error('Chart.js wurde geladen, aber nicht initialisiert.'));
        }, { once: true });
        script.addEventListener('error', () => reject(new Error('Chart.js konnte nicht geladen werden.')), { once: true });
        document.head.appendChild(script);
    }).catch((error) => {
        chartLoaderPromise = null;
        throw error;
    });

    return chartLoaderPromise;
};

const cssValue = (element, variable, fallback) => {
    const value = getComputedStyle(element).getPropertyValue(variable).trim();
    return value || fallback;
};

const hexToRgba = (value, alpha) => {
    const match = /^#([0-9a-f]{6})$/i.exec(value.trim());
    if (!match) {
        return value;
    }

    const hex = match[1];
    const red = Number.parseInt(hex.slice(0, 2), 16);
    const green = Number.parseInt(hex.slice(2, 4), 16);
    const blue = Number.parseInt(hex.slice(4, 6), 16);

    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

const chartPalette = (canvas) => {
    const clubPage = canvas.closest('.public-club-page') ?? document.documentElement;
    const root = document.documentElement;
    const primary = cssValue(clubPage, '--club-primary', '#0d6efd');
    const text = cssValue(root, '--bs-body-color', '#212529');
    const muted = cssValue(root, '--bs-secondary-color', '#6c757d');
    const border = cssValue(root, '--bs-border-color', '#dee2e6');
    const info = cssValue(root, '--bs-info', '#0dcaf0');

    return {
        primary,
        primaryFill: hexToRgba(primary, 0.10),
        text,
        muted,
        border: hexToRgba(border, 0.55),
        secondary: info,
        secondaryFill: hexToRgba(info, 0.08),
    };
};

const readChartConfig = (canvas) => {
    const configId = canvas.dataset.chartConfigId;
    if (!configId) {
        return null;
    }

    const source = document.getElementById(configId);
    if (!source) {
        return null;
    }

    try {
        return JSON.parse(source.textContent || '{}');
    } catch (error) {
        console.error('Ungültige Vereinschart-Konfiguration.', error);
        return null;
    }
};

const commonOptions = (palette) => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: {
        duration: 260,
    },
    interaction: {
        intersect: false,
        mode: 'index',
    },
    plugins: {
        legend: {
            labels: {
                color: palette.text,
                boxWidth: 12,
                boxHeight: 3,
                usePointStyle: true,
                pointStyle: 'line',
                font: {
                    size: 11,
                },
            },
        },
        tooltip: {
            displayColors: true,
        },
    },
});

const currentSeasonConfiguration = (config, palette) => {
    const points = Array.isArray(config.points) ? config.points : [];
    const maxRank = Math.max(
        2,
        Number(config.teamCount || 0),
        ...points.map((point) => Number(point.rank || 1)),
    );

    const options = commonOptions(palette);
    options.plugins.legend.display = false;
    options.plugins.tooltip.callbacks = {
        title: (items) => {
            const point = points[items[0]?.dataIndex ?? 0];
            return point ? `${config.labels.matchday} ${point.matchday}` : '';
        },
        label: (context) => `${config.labels.rank} ${context.parsed.y}`,
        afterLabel: (context) => {
            const point = points[context.dataIndex];
            return point ? `${point.played} ${config.labels.played}` : '';
        },
    };
    options.scales = {
        x: {
            grid: {
                display: false,
            },
            ticks: {
                color: palette.muted,
                maxRotation: 0,
                autoSkip: true,
                maxTicksLimit: 10,
            },
            border: {
                color: palette.border,
            },
        },
        y: {
            reverse: true,
            min: 1,
            max: maxRank,
            ticks: {
                color: palette.muted,
                stepSize: 1,
                precision: 0,
                callback: (value) => `${value}.`,
            },
            grid: {
                color: palette.border,
            },
            border: {
                display: false,
            },
        },
    };

    return {
        type: 'line',
        data: {
            labels: points.map((point) => String(point.matchday)),
            datasets: [{
                label: config.labels.rank,
                data: points.map((point) => Number(point.rank || 1)),
                borderColor: palette.primary,
                backgroundColor: palette.primaryFill,
                fill: true,
                tension: 0.28,
                borderWidth: 2.5,
                pointRadius: 4,
                pointHoverRadius: 6,
                pointBackgroundColor: palette.primary,
                pointBorderColor: palette.primary,
            }],
        },
        options,
    };
};

const historyConfiguration = (config, palette) => {
    const points = Array.isArray(config.points) ? config.points : [];
    const maxLeague = Math.max(2, ...points.map((point) => Number(point.leagueRank || 1)));
    const maxRank = Math.max(2, 16, ...points.map((point) => Number(point.rank || 1)));

    const options = commonOptions(palette);
    options.plugins.tooltip.callbacks = {
        title: (items) => {
            const point = points[items[0]?.dataIndex ?? 0];
            if (!point) {
                return '';
            }
            return point.current ? `${point.season} · ${config.labels.current}` : point.season;
        },
        label: (context) => {
            if (context.dataset.yAxisID === 'league') {
                return `${config.labels.league}: ${context.parsed.y}`;
            }
            return `${config.labels.seasonRank}: ${context.parsed.y}`;
        },
    };
    options.scales = {
        x: {
            grid: {
                display: false,
            },
            ticks: {
                color: palette.muted,
                maxRotation: 0,
                autoSkip: true,
                maxTicksLimit: 8,
            },
            border: {
                color: palette.border,
            },
        },
        league: {
            type: 'linear',
            position: 'left',
            reverse: true,
            min: 1,
            max: maxLeague,
            title: {
                display: true,
                text: config.labels.league,
                color: palette.muted,
                font: {
                    size: 10,
                    weight: '600',
                },
            },
            ticks: {
                color: palette.muted,
                stepSize: 1,
                precision: 0,
                callback: (value) => `${value}.`,
            },
            grid: {
                color: palette.border,
            },
            border: {
                display: false,
            },
        },
        rank: {
            type: 'linear',
            position: 'right',
            reverse: true,
            min: 1,
            max: maxRank,
            title: {
                display: true,
                text: config.labels.seasonRank,
                color: palette.muted,
                font: {
                    size: 10,
                    weight: '600',
                },
            },
            ticks: {
                color: palette.muted,
                stepSize: 1,
                precision: 0,
                callback: (value) => `${value}.`,
            },
            grid: {
                drawOnChartArea: false,
            },
            border: {
                display: false,
            },
        },
    };

    return {
        type: 'line',
        data: {
            labels: points.map((point) => point.season),
            datasets: [
                {
                    label: config.labels.league,
                    yAxisID: 'league',
                    data: points.map((point) => Number(point.leagueRank || 1)),
                    borderColor: palette.primary,
                    backgroundColor: palette.primaryFill,
                    tension: 0.22,
                    borderWidth: 2.5,
                    pointRadius: (context) => points[context.dataIndex]?.current ? 6 : 4,
                    pointHoverRadius: 7,
                    pointBackgroundColor: palette.primary,
                    pointBorderColor: palette.primary,
                },
                {
                    label: config.labels.seasonRank,
                    yAxisID: 'rank',
                    data: points.map((point) => Number(point.rank || 1)),
                    borderColor: palette.secondary,
                    backgroundColor: palette.secondaryFill,
                    tension: 0.22,
                    borderWidth: 2,
                    borderDash: [5, 4],
                    pointRadius: (context) => points[context.dataIndex]?.current ? 6 : 3.5,
                    pointHoverRadius: 6,
                    pointBackgroundColor: palette.secondary,
                    pointBorderColor: palette.secondary,
                },
            ],
        },
        options,
    };
};

const renderChart = (Chart, canvas) => {
    const config = readChartConfig(canvas);
    if (!config || !Array.isArray(config.points) || config.points.length === 0) {
        return;
    }

    const existing = chartInstances.get(canvas);
    if (existing) {
        existing.destroy();
        chartInstances.delete(canvas);
    }

    const palette = chartPalette(canvas);
    const chartConfig = config.mode === 'history'
        ? historyConfiguration(config, palette)
        : currentSeasonConfiguration(config, palette);
    const chart = new Chart(canvas.getContext('2d'), chartConfig);
    chartInstances.set(canvas, chart);
    canvas.dataset.chartInitialized = 'true';

    const fallback = canvas.closest('[data-public-club-chart-stage]')
        ?.querySelector('[data-public-club-chart-fallback]');
    fallback?.classList.add('d-none');
};

const renderClubCharts = async (root = document) => {
    const canvases = [...root.querySelectorAll('[data-public-club-chart]')];
    if (canvases.length === 0) {
        return;
    }

    try {
        const Chart = await loadChartJs();
        canvases.forEach((canvas) => renderChart(Chart, canvas));
    } catch (error) {
        console.error(error);
        canvases.forEach((canvas) => {
            const config = readChartConfig(canvas);
            const fallback = canvas.closest('[data-public-club-chart-stage]')
                ?.querySelector('[data-public-club-chart-fallback]');
            if (!fallback) {
                return;
            }
            fallback.classList.remove('d-none');
            const text = fallback.querySelector('span');
            if (text) {
                text.textContent = config?.labels?.loadError ?? 'Diagramm konnte nicht geladen werden.';
            }
        });
    }
};

const observeThemeChanges = () => {
    if (themeObserver) {
        return;
    }

    themeObserver = new MutationObserver(() => {
        if (!window.Chart) {
            return;
        }
        chartInstances.forEach((chart, canvas) => {
            if (!canvas.isConnected) {
                chart.destroy();
                chartInstances.delete(canvas);
                return;
            }
            renderChart(window.Chart, canvas);
        });
    });

    themeObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['data-bs-theme'],
    });
};

const initializePublicPortal = (root = document) => {
    observeThemeChanges();
    renderClubCharts(root);
};

export {
    initializePublicPortal,
};
